// fire.js or fire.ts
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { resolveFirebaseEnvironment } from './firebaseEnvironment';

// Preview mode users — the br-token is sent as JSON.stringify(user) to the API,
// where the mock firebase-admin parses it back with JSON.parse().
const PREVIEW_USERS = [
  { uid: 'author1', name: 'Author One', email: 'author.one@betareader.io', email_verified: true },
  { uid: 'author2', name: 'Author Two', email: 'author.two@betareader.io', email_verified: true },
  { uid: 'author3', name: 'Author Three', email: 'author.three@betareader.io', email_verified: true },
  { uid: 'reader1', name: 'Reader One', email: 'reader.one@betareader.io', email_verified: true },
  { uid: 'reader2', name: 'Reader Two', email: 'reader.two@betareader.io', email_verified: true },
  { uid: 'reader3', name: 'Reader Three', email: 'reader.three@betareader.io', email_verified: true },
];

const isPreviewMode = process.env.REACT_APP_PREVIEW_MODE === 'true';

let _firebase: any;

if (isPreviewMode) {
  // Preview mode: create a mock firebase object that bypasses real auth.
  // The selected user index is stored in localStorage so it persists across reloads.
  const getSelectedUserIndex = (): number => {
    try {
      const stored = window.localStorage.getItem('br-preview-user-index');
      if (stored !== null) {
        const idx = parseInt(stored, 10);
        if (idx >= 0 && idx < PREVIEW_USERS.length) return idx;
      }
    } catch (e) { /* ignore */ }
    return 0; // default to author1
  };

  const getSelectedUser = () => PREVIEW_USERS[getSelectedUserIndex()];

  const makeCurrentUser = () => {
    const user = getSelectedUser();
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.name,
      emailVerified: user.email_verified,
      isAnonymous: false,
      providerData: [{ providerId: 'password' }],
      reload: () => Promise.resolve(),
      getIdToken: () => Promise.resolve(JSON.stringify(user)),
      toJSON: () => user,
    };
  };

  // Store auth state change callbacks so switchPreviewUser can trigger them
  const authCallbacks: Array<(user: any) => void> = [];
  const idTokenCallbacks: Array<(user: any) => void> = [];

  const mockAuth: any = {
    currentUser: makeCurrentUser(),
    onAuthStateChanged: (callback: (user: any) => void) => {
      authCallbacks.push(callback);
      // Call immediately with current user (mimics Firebase behavior)
      setTimeout(() => callback(makeCurrentUser()), 0);
      // Return unsubscribe function
      return () => {
        const idx = authCallbacks.indexOf(callback);
        if (idx >= 0) authCallbacks.splice(idx, 1);
      };
    },
    onIdTokenChanged: (callback: (user: any) => void) => {
      idTokenCallbacks.push(callback);
      setTimeout(() => callback(makeCurrentUser()), 0);
      return () => {
        const idx = idTokenCallbacks.indexOf(callback);
        if (idx >= 0) idTokenCallbacks.splice(idx, 1);
      };
    },
    signInWithEmailAndPassword: () => Promise.resolve({ user: makeCurrentUser() }),
    signInWithCustomToken: () => Promise.resolve({ user: makeCurrentUser() }),
    signInWithPopup: () => Promise.resolve({ user: makeCurrentUser() }),
    signInWithRedirect: () => Promise.resolve(),
    signOut: () => {
      window.localStorage.removeItem('br-preview-user-index');
      return Promise.resolve();
    },
    getRedirectResult: () => Promise.resolve({}),
    sendSignInLinkToEmail: () => Promise.resolve(),
    signInWithEmailLink: () => Promise.resolve(),
  };

  // Expose a function to switch preview users (called by the PreviewUserSwitcher component)
  (window as any).__switchPreviewUser = (index: number) => {
    window.localStorage.setItem('br-preview-user-index', String(index));
    mockAuth.currentUser = makeCurrentUser();
    // Notify all listeners
    const user = makeCurrentUser();
    authCallbacks.forEach(cb => cb(user));
    idTokenCallbacks.forEach(cb => cb(user));
  };

  // Expose preview users for the switcher UI
  (window as any).__PREVIEW_USERS = PREVIEW_USERS;
  (window as any).__getPreviewUserIndex = getSelectedUserIndex;

  const mockFirebase: any = {
    apps: [{}], // Pretend we have an app initialized
    initializeApp: () => {},
    auth: () => mockAuth,
  };

  console.log('[preview] Firebase auth mocked — logged in as', getSelectedUser().name);
  _firebase = mockFirebase;
} else {
  // Normal mode
  const prodConfig = {
    apiKey: 'AIzaSyAoHHwwhc6NcNDzUnqr99XQDRT9OhHwUXc',
    authDomain: 'app.betareader.io',
    databaseURL: 'https://beta-reader-prod.firebaseio.com',
    projectId: 'beta-reader-prod',
    storageBucket: 'beta-reader-prod.appspot.com',
    messagingSenderId: '731030581916',
  };

  const devConfig = {
    apiKey: 'AIzaSyC-uMiCqVJT4pxbrtMSegXEyI-BRn5fff8',
    authDomain: 'betareader-dev.firebaseapp.com',
    databaseURL: 'https://betareader-dev.firebaseio.com',
    projectId: 'betareader-dev',
    storageBucket: '',
    messagingSenderId: '696645853516',
    appId: '1:696645853516:web:be7ad1b31c68d674',
  };

  const firebaseEnvironment = resolveFirebaseEnvironment({
    configuredEnvironment: process.env.REACT_APP_FIREBASE_ENV,
    nodeEnvironment: process.env.NODE_ENV,
  });
  const config = firebaseEnvironment === 'staging' ? devConfig : prodConfig;

  if (!firebase.apps.length) {
    console.log('Initializing Firebase');
    firebase.initializeApp(config);
  }

  _firebase = firebase;
}

export default _firebase;
